import { useCallback, useEffect, useState } from "react";
import { Mail, X, Check } from "lucide-react";
import { listarMensagens, atualizarMensagem } from "../../lib/adminApi";
import { Card, PageTitle, ClinicaLabel, formatDate } from "../ui";

const ESTADO_LABEL = { nova: "Nova", lida: "Lida", respondida: "Respondida" };

export default function AdminMensagensPage() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroClinica, setFiltroClinica] = useState("");
  const [sel, setSel] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroClinica) params.clinica = filtroClinica;
      setData(await listarMensagens(params));
    } finally { setLoading(false); }
  }, [filtroEstado, filtroClinica]);

  useEffect(() => { carregar(); }, [carregar]);

  const marcar = async (id, estado) => {
    await atualizarMensagem(id, estado);
    setSel((s) => s && s.id === id ? { ...s, estado } : s);
    carregar();
  };

  return (
    <div data-testid="admin-mensagens">
      <PageTitle subtitle="Mensagens recebidas pelo formulário de contacto.">Mensagens</PageTitle>

      <Card className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-widest font-bold text-[#88A096] mr-2">Estado:</span>
          {["", "nova", "lida", "respondida"].map((e) => (
            <button
              key={e || "todos"}
              onClick={() => setFiltroEstado(e)}
              data-testid={`filter-estado-${e || "todos"}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold border ${filtroEstado === e ? "bg-[#1E5F6E] text-white border-[#1E5F6E]" : "bg-white text-[#5A5A5A] border-[#1E5F6E]/15"}`}
            >{e === "" ? "Todos" : ESTADO_LABEL[e]}</button>
          ))}
          <span className="text-xs uppercase tracking-widest font-bold text-[#88A096] ml-4 mr-2">Clínica:</span>
          {["", "ribeira-brava", "calheta"].map((c) => (
            <button
              key={c || "todas"}
              onClick={() => setFiltroClinica(c)}
              data-testid={`filter-clinica-${c || "todas"}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold border ${filtroClinica === c ? "bg-[#1E5F6E] text-white border-[#1E5F6E]" : "bg-white text-[#5A5A5A] border-[#1E5F6E]/15"}`}
            >{c === "" ? "Todas" : c === "ribeira-brava" ? "Ribeira Brava" : "Calheta"}</button>
          ))}
        </div>
      </Card>

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-xs uppercase tracking-wider text-[#5A5A5A]">
              <tr>
                <th className="text-left py-3 px-4">Data</th>
                <th className="text-left py-3 px-4">Nome</th>
                <th className="text-left py-3 px-4">Assunto</th>
                <th className="text-left py-3 px-4">Clínica</th>
                <th className="text-left py-3 px-4">Estado</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="py-6 text-center text-[#5A5A5A]">A carregar...</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#5A5A5A]">Sem mensagens.</td></tr>}
              {!loading && data.items.map((m) => (
                <tr key={m.id} onClick={() => { setSel(m); if (m.estado === "nova") marcar(m.id, "lida"); }} data-testid={`msg-row-${m.id}`} className="border-t border-[#1E5F6E]/10 hover:bg-[#FAF7F2] cursor-pointer">
                  <td className="py-3 px-4">{formatDate(m.created_at)}</td>
                  <td className="py-3 px-4 font-semibold">{m.nome}<div className="text-xs text-[#5A5A5A]">{m.email}</div></td>
                  <td className="py-3 px-4">{m.assunto}</td>
                  <td className="py-3 px-4">{m.clinica_relacionada ? <ClinicaLabel slug={m.clinica_relacionada} /> : "—"}</td>
                  <td className="py-3 px-4">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold border ${
                      m.estado === "nova" ? "bg-amber-50 text-amber-700 border-amber-200"
                        : m.estado === "lida" ? "bg-sky-50 text-sky-700 border-sky-200"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`}>{ESTADO_LABEL[m.estado]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {sel && (
        <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={() => setSel(null)} data-testid="msg-drawer">
          <div className="bg-white w-full max-w-md h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-xs uppercase font-bold text-[#88A096] tracking-widest">Mensagem</div>
                <h3 className="font-display text-xl font-bold">{sel.assunto}</h3>
              </div>
              <button onClick={() => setSel(null)} className="h-8 w-8 grid place-items-center rounded-lg bg-[#FAF7F2]"><X className="h-4 w-4" /></button>
            </div>
            <div className="text-sm space-y-2">
              <div><strong>De:</strong> {sel.nome} <a href={`mailto:${sel.email}`} className="text-[#1E5F6E] font-bold ml-1">({sel.email})</a></div>
              {sel.telefone && <div><strong>Telefone:</strong> <a href={`tel:${sel.telefone}`} className="text-[#1E5F6E] font-bold">{sel.telefone}</a></div>}
              <div><strong>Data:</strong> {formatDate(sel.created_at)}</div>
              {sel.clinica_relacionada && <div><strong>Clínica:</strong> <ClinicaLabel slug={sel.clinica_relacionada} /></div>}
              <div className="mt-4 rounded-xl bg-[#FAF7F2] border border-[#1E5F6E]/10 p-3 whitespace-pre-line">{sel.mensagem}</div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <a href={`mailto:${sel.email}?subject=Re: ${encodeURIComponent(sel.assunto)}`} data-testid="msg-responder" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-4 py-2 text-xs font-bold">
                <Mail className="h-3 w-3" /> Responder por email
              </a>
              <button onClick={() => marcar(sel.id, "respondida")} data-testid="msg-marcar-respondida" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-4 py-2 text-xs font-bold">
                <Check className="h-3 w-3" /> Marcar respondida
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
